import React, {useEffect, useRef, useState} from 'react'

import ActionsCard, {ActionError, placeLabel} from './ActionsCard.js'
import MapCard from './MapCard.js'
import {trackTraceCss} from './styles.js'
import {themeVariables} from './theme.js'
import {Translator, defaultMessages, languageNames, translator} from './i18n.js'
import {TrackTraceActionKey, TrackTraceActionRequest, TrackTraceColloState, TrackTraceEvent, TrackTraceLanguage, TrackTraceView} from '../../types/trackTrace.js'

interface Props {
    view: TrackTraceView
    language: TrackTraceLanguage
    onLanguageChange: (language: TrackTraceLanguage) => void
    // Resolves with an error code, or null when the recipient's change was saved
    onAction: (request: TrackTraceActionRequest) => Promise<ActionError | null>
    updatedAt?: Date
    // Rendered inside the planner settings: no fixed overlays
    preview?: boolean
    // The option a link from a mail opens (?actie=), only on the first render of the actions
    initialAction?: TrackTraceActionKey | null
}

const WARN_STATES = ['notCompleted', 'returning', 'onHold', 'cancelled']

function capitalize(text: string) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

function Icon({name}: {name: string}) {
    return <i className={`mdi mdi-${name}`} aria-hidden='true' />
}

function moment(view: TrackTraceView, tr: Translator): {eyebrow: string, title: string, sub: string} {
    const {t} = tr
    const pickup = view.addressType === 'pickup'
    const windowText = view.window ? t('subWindow', {from: view.window.from, to: view.window.to}) : ''
    const choice = view.details?.actions?.choice

    switch (view.state) {
        case 'scheduled':
            return {
                eyebrow: t(pickup ? 'eyebrowScheduledPickup' : 'eyebrowScheduledDelivery'),
                title: view.date ? tr.relativeDate(view.date) : t('titleDateUnknown'),
                sub: windowText
            }
        case 'onTheWay':
            return {
                eyebrow: t(pickup ? 'eyebrowOnTheWayPickup' : 'eyebrowOnTheWayDelivery'),
                title: view.window ? `${view.date ? `${tr.relativeDate(view.date)} · ` : ''}${view.window.from} – ${view.window.to}` : view.date ? tr.relativeDate(view.date) : t('titleDateUnknown'),
                sub: typeof view.map?.stopsBefore === 'number' ? `${t('subOnTheWay')} ${t('mapStops', {count: view.map.stopsBefore})}` : t('subOnTheWay')
            }
        case 'completed': {
            let sub = t(pickup ? 'subCompletedPickup' : 'subCompletedDelivery')

            if (view.outcome?.safePlace) {
                sub = t('subSafePlace', {place: placeLabel(view.outcome.safePlace, tr)})
            } else if (view.outcome?.mailbox) {
                sub = t('subMailbox')
            } else if (view.outcome) {
                sub = view.outcome.neighbourNr ? t('subNeighbour', {nr: view.outcome.neighbourNr}) : t('subNeighbourUnknown')
            }

            return {
                eyebrow: t(pickup ? 'eyebrowCompletedPickup' : 'eyebrowCompletedDelivery'),
                title: view.date && view.arrivedAt ? t('titleCompletedAt', {date: tr.relativeDate(view.date), time: view.arrivedAt}) : view.date ? tr.relativeDate(view.date) : t(pickup ? 'eyebrowCompletedPickup' : 'eyebrowCompletedDelivery'),
                sub
            }
        }
        case 'notCompleted':
            return {
                eyebrow: t(pickup ? 'eyebrowNotCompletedPickup' : 'eyebrowNotCompletedDelivery'),
                title: view.message?.key.includes('NotHome') ? t('titleNotCompleted') : t('titleAttemptFailed'),
                sub: ''
            }
        case 'returning':
            return {eyebrow: t('eyebrowReturning'), title: t('titleReturning'), sub: ''}
        case 'onHold':
            if (choice?.type === 'pickupPoint') {
                return {eyebrow: t('eyebrowPickupPoint'), title: capitalize(t('titleOnHoldUntil', {date: tr.date(choice.until)})), sub: choice.pickupPoint.name}
            }
            return {eyebrow: t('eyebrowOnHold'), title: view.date ? capitalize(t('titleOnHoldUntil', {date: tr.date(view.date)})) : t('eyebrowOnHold'), sub: ''}
        case 'cancelled':
            return {eyebrow: t('eyebrowCancelled'), title: t('titleCancelled'), sub: ''}
        default:
            return {eyebrow: t('eyebrowExpected'), title: t('titleDateUnknown'), sub: t('subExpected')}
    }
}

function colloLabel(state: TrackTraceColloState, pickup: boolean, tr: Translator) {
    const keys = {
        expected: 'colloExpected',
        atDepot: 'colloAtDepot',
        onTheWay: 'colloOnTheWay',
        completed: pickup ? 'colloCompletedPickup' : 'colloCompletedDelivery',
        notCompleted: 'colloNotCompleted',
        onHold: 'colloOnHold',
        returned: 'colloReturned',
        cancelled: 'colloCancelled',
        missing: 'colloMissing'
    } as const
    return tr.t(keys[state] || 'colloExpected')
}

function eventLabel(type: TrackTraceEvent['type'], pickup: boolean, tr: Translator) {
    const keys = {
        created: 'eventCreated',
        atDepot: 'eventAtDepot',
        planned: 'eventPlanned',
        onTheWay: 'eventOnTheWay',
        rescheduled: 'eventRescheduled',
        completed: pickup ? 'eventCompletedPickup' : 'eventCompletedDelivery',
        notCompleted: pickup ? 'eventNotCompletedPickup' : 'eventNotCompletedDelivery',
        onHold: 'eventOnHold',
        returned: 'eventReturned',
        cancelled: 'eventCancelled',
        manco: 'eventManco'
    } as const
    return tr.t(keys[type])
}

// "Niet thuis" or "Geweigerd", with the reason specification of the reseller when there is one
function eventReason(event: TrackTraceEvent, tr: Translator) {
    return [event.reason ? tr.dict.reasons[event.reason] : '', event.reasonNote || ''].filter(Boolean).join(' · ')
}

function LanguageSwitcher({languages, language, onChange, label}: {languages: TrackTraceLanguage[], language: TrackTraceLanguage, onChange: (language: TrackTraceLanguage) => void, label: string}) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return

        const close = (event: MouseEvent | KeyboardEvent) => {
            if (event instanceof KeyboardEvent ? event.key === 'Escape' : !ref.current?.contains(event.target as Node)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', close)
        document.addEventListener('keydown', close)
        return () => {
            document.removeEventListener('mousedown', close)
            document.removeEventListener('keydown', close)
        }
    }, [open])

    if (languages.length < 2) {
        return null
    }

    return (
        <div className='tt-lang' ref={ref}>
            <button type='button' className='tt-lang-button' aria-label={label} aria-expanded={open} aria-haspopup='true' onClick={() => setOpen(!open)}>
                <Icon name='web' />
                {language.toUpperCase()}
            </button>

            {open &&
                <ul className='tt-lang-menu'>
                    {languages.map((code) => (
                        <li key={code}>
                            <button
                                type='button'
                                lang={code}
                                aria-current={code === language}
                                onClick={() => {
                                    setOpen(false)
                                    onChange(code)
                                }}
                            >
                                {languageNames[code]}
                                {code === language && <Icon name='check' />}
                            </button>
                        </li>
                    ))}
                </ul>
            }
        </div>
    )
}

export default function TrackTracePage({view, language, onLanguageChange, onAction, updatedAt, preview, initialAction}: Props) {
    const [zoomed, setZoomed] = useState<string | null>(null)
    const [openFaq, setOpenFaq] = useState<number | null>(null)
    const [showParcels, setShowParcels] = useState(false)

    useEffect(() => {
        if (!zoomed) return

        const close = (event: KeyboardEvent) => event.key === 'Escape' && setZoomed(null)
        document.addEventListener('keydown', close)
        return () => document.removeEventListener('keydown', close)
    }, [zoomed])

    const tr = translator(language)
    const {t} = tr
    const {branding, details} = view
    const pickup = view.addressType === 'pickup'
    const warn = WARN_STATES.includes(view.state)
    const text = moment(view, tr)

    const steps = pickup ? tr.dict.stepsPickup : tr.dict.stepsDelivery
    const progressLabel = warn ? text.eyebrow : `${t('stepOf', {step: view.step + 1, total: steps.length})} · ${steps[view.step]}`
    const message = view.message ? view.message.text || defaultMessages(language)[view.message.key] : ''

    const address = details?.address
    const addressText = address ?
        [`${address.street} ${address.nr}${address.addition}`.trim(), address.street2, `${address.postalCode} ${address.city}`.trim()].filter(Boolean).join(', ') :
        view.city

    const requirements = [
        view.requirements.signature && t('reqSignature'),
        view.requirements.statedAddressOnly && t('reqStatedAddressOnly'),
        view.requirements.mailbox && t('reqMailbox'),
        view.requirements.minimumAge && t('reqMinimumAge', {age: view.requirements.minimumAge}),
        view.requirements.idCheck && t('reqIdCheck'),
        view.requirements.verificationCode && t('reqVerificationCode')
    ].filter(Boolean) as string[]
    const showRequirements = requirements.length > 0 && ['expected', 'scheduled', 'onTheWay'].includes(view.state)

    const proof = details?.proof
    const showProof = view.state === 'completed' && !!proof && !!(proof.signee || proof.signature || proof.images.length)

    // Hidden when nothing can be changed any more and there is nothing to show
    const actions = details?.actions
    const showActions = !!actions && ([actions.safePlace, actions.neighbour, actions.pickupPoint, actions.instructions, actions.deliveryDay].some((action) => action?.available) || !!actions.choice || !!actions.instructionsText)

    const timeline = [...view.timeline].reverse()
    const parcelCount = view.colli.length

    const whatsappNumber = branding.whatsapp.replace(/[^\d]/g, '').replace(/^0(?=6)/, '31')
    const hasContact = branding.phone || branding.whatsapp || branding.email
    const recipientName = address ? [address.name, address.attention].filter(Boolean).join(', ') : ''

    const photo = branding.photo
    const logo = branding.logoUrl ? <img className='tt-logo' src={branding.logoUrl} alt={branding.resellerName || 'Logo'} /> : <span className='tt-logo-name'>{branding.resellerName}</span>

    // On desktop the timeline is a column next to the cards below the status, spanning exactly their rows
    const timelineRow = photo?.mobile || photo?.desktop ? 3 : 2
    const besideTimeline = [showActions, !!(branding.message || view.map), showProof, showRequirements, !!(hasContact || branding.faq.length)].filter(Boolean).length

    return (
        <div className='tt' style={themeVariables(branding.color)} lang={language}>
            <style>{trackTraceCss}</style>

            <header className='tt-topbar'>
                <div className='tt-topbar-inner'>
                    {logo}
                    <div className='tt-topbar-end'>
                        <div className='tt-topbar-code'>
                            <span className='tt-body-small'>{t('trackTraceCode')}</span>
                            <span className='tt-title-small'>{view.trackTrace}</span>
                        </div>
                        <LanguageSwitcher languages={branding.languages} language={language} onChange={onLanguageChange} label={t('changeLanguage')} />
                    </div>
                </div>
            </header>

            <div className='tt-band' />

            <div className='tt-body'>
                <main className='tt-main'>
                    {photo?.mobile &&
                        <div className='tt-card tt-photo tt-photo-mobile tt-wide'><img src={photo.mobile} alt={t('carrierPhoto', {name: branding.resellerName})} /></div>
                    }
                    {photo?.desktop &&
                        <div className='tt-card tt-photo tt-photo-desktop tt-wide'><img src={photo.desktop} alt={t('carrierPhoto', {name: branding.resellerName})} /></div>
                    }

                    <section className='tt-card tt-wide' aria-label={text.eyebrow}>
                        <div className='tt-progress'>
                            <div className='tt-segments' role='progressbar' aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={view.step + 1} aria-valuetext={progressLabel}>
                                {steps.map((step, index) => (
                                    <span key={step} className={`tt-segment${index < view.step || (index === view.step && !warn) ? ' tt-segment-done' : index === view.step ? ' tt-segment-warn' : ''}`} />
                                ))}
                            </div>
                            <p className='tt-label-medium'>{progressLabel}</p>
                        </div>

                        <div className='tt-status-head'>
                            <div className='tt-moment'>
                                <p className='tt-title-small'>{text.eyebrow}</p>
                                <h1 className='tt-headline'>{text.title}</h1>
                                {text.sub && <p className={view.state === 'scheduled' ? 'tt-subline' : 'tt-moment-sub'}>{text.sub}</p>}
                            </div>

                            <ol className='tt-stepper' aria-hidden='true'>
                                {steps.map((step, index) => {
                                    const done = index < view.step || (index === view.step && view.step === steps.length - 1 && !warn)

                                    return (
                                        <li key={step} className={`tt-step${done ? ' tt-step-done' : index === view.step ? ` tt-step-current${warn ? ' tt-step-warn' : ''}` : ''}`}>
                                            <span className='tt-step-dot'>{done && <Icon name='check' />}</span>
                                            {step}
                                        </li>
                                    )
                                })}
                            </ol>
                        </div>

                        {message &&
                            <div className='tt-notice' role='status'>
                                <Icon name='alert-outline' />
                                <p>{message}</p>
                            </div>
                        }

                        <hr className='tt-divider' />

                        <div className='tt-rows'>
                            <div className='tt-row'>
                                <Icon name='map-marker-outline' />
                                <div className='tt-row-text'>
                                    <span className='tt-body-small'>{t(pickup ? 'pickupAddress' : 'deliveryAddress')}</span>
                                    <span className='tt-value'>{addressText}</span>
                                </div>
                            </div>
                            {parcelCount > 0 &&
                                <div className='tt-row'>
                                    <Icon name='package-variant-closed' />
                                    <div className='tt-row-text'>
                                        <span className='tt-body-small'>{t('shipment')}</span>
                                        <span className='tt-value'>{view.senderName ? t('shipmentFrom', {count: tr.plural(tr.dict.parcels, parcelCount), sender: view.senderName}) : tr.plural(tr.dict.parcels, parcelCount)}</span>
                                        <button type='button' className='tt-toggle' aria-expanded={showParcels} aria-controls='tt-details' onClick={() => setShowParcels(!showParcels)}>
                                            {t(showParcels ? 'lessPackageInfo' : 'morePackageInfo')}
                                            <Icon name={showParcels ? 'chevron-up' : 'chevron-down'} />
                                        </button>
                                    </div>
                                </div>
                            }
                        </div>

                        {showParcels &&
                            <div id='tt-details' className='tt-details'>
                                <hr className='tt-divider' />
                                <dl className='tt-grid'>
                                    {view.senderName &&
                                        <div className='tt-row-text'><dt className='tt-body-small'>{t('sender')}</dt><dd className='tt-value'>{view.senderName}</dd></div>
                                    }
                                    {details?.reference &&
                                        <div className='tt-row-text'><dt className='tt-body-small'>{t('reference')}</dt><dd className='tt-value'>{details.reference}</dd></div>
                                    }
                                    <div className='tt-row-text'><dt className='tt-body-small'>{t('trackTraceCode')}</dt><dd className='tt-value'>{view.trackTrace}</dd></div>
                                    {recipientName &&
                                        <div className='tt-row-text'><dt className='tt-body-small'>{t('recipient')}</dt><dd className='tt-value'>{recipientName}</dd></div>
                                    }
                                </dl>

                                <div>
                                    {view.colli.map((collo, index) => {
                                        const meta = details?.colli[index] ? [details.colli[index].barcode, details.colli[index].type].filter(Boolean).join(' · ') : ''
                                        return (
                                            <div key={index} className='tt-collo'>
                                                <div className='tt-row-text'>
                                                    <span className='tt-title-small'>{t('parcelOf', {n: index + 1, total: parcelCount})}</span>
                                                    {meta && <span className='tt-body-small'>{meta}</span>}
                                                </div>
                                                <span className='tt-badge'>{colloLabel(collo.state, pickup, tr)}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        }
                    </section>

                    {showActions && <ActionsCard key={`${language}-${view.trackTrace}-${actions.choice?.type || ''}`} tr={tr} actions={actions} resellerName={branding.resellerName} onAction={onAction} initialPanel={initialAction} />}

                    {(branding.message || view.map) &&
                        <div className='tt-pair'>
                            {branding.message &&
                                <section className='tt-card tt-message' aria-label={t('teamOf', {name: branding.resellerName})}>
                                    {logo}
                                    <div className='tt-row-text' style={{gap: 3, alignItems: 'center'}}>
                                        <p className='tt-message-text'>{branding.message}</p>
                                        <p className='tt-body-small'>{t('teamOf', {name: branding.resellerName})}</p>
                                    </div>
                                </section>
                            }
                            {view.map && <MapCard tr={tr} map={view.map} courierIcon={branding.outForDeliveryIcon} />}
                        </div>
                    }

                    {showProof &&
                        <section className='tt-card' aria-labelledby='tt-proof-title'>
                            <h2 id='tt-proof-title' className='tt-title-medium'>{t(pickup ? 'proofTitlePickup' : 'proofTitleDelivery')}</h2>

                            {proof.images.length > 0 &&
                                <div className='tt-proof-images'>
                                    {proof.images.map((image, index) => (
                                        <button key={index} type='button' className='tt-proof-image' aria-label={`${t('enlarge')}: ${t('photo', {n: index + 1})}`} onClick={() => !preview && setZoomed(image)}>
                                            <img src={image} alt={t('photo', {n: index + 1})} />
                                        </button>
                                    ))}
                                </div>
                            }
                            {proof.signee &&
                                <div className='tt-row-text'>
                                    <span className='tt-body-small'>{t('signee')}</span>
                                    <span className='tt-value'>{proof.signee}</span>
                                </div>
                            }
                            {proof.signature &&
                                <div className='tt-row-text'>
                                    <span className='tt-body-small'>{t('signature')}</span>
                                    <img className='tt-signature' src={proof.signature} alt={t('signature')} />
                                </div>
                            }
                        </section>
                    }

                    {showRequirements &&
                        <section className='tt-card' aria-labelledby='tt-requirements-title'>
                            <h2 id='tt-requirements-title' className='tt-title-medium'>{t('requirementsTitle')}</h2>
                            <ul className='tt-list'>
                                {requirements.map((requirement) => <li key={requirement}><Icon name='information-outline' />{requirement}</li>)}
                            </ul>
                        </section>
                    }

                    {timeline.length > 0 &&
                        <section className='tt-card tt-side' style={{'--tt-side-row': `${timelineRow} / span ${Math.max(besideTimeline, 1)}`} as React.CSSProperties} aria-labelledby='tt-timeline-title'>
                            <h2 id='tt-timeline-title' className='tt-title-medium'>{t('timelineTitle')}</h2>
                            <ol className='tt-timeline'>
                                {timeline.map((event, index) => (
                                    <li key={`${event.type}-${event.timestamp}`} className='tt-event'>
                                        <div className='tt-event-rail'>
                                            <span className={`tt-event-dot${index === 0 ? (warn || event.type === 'notCompleted' ? ' tt-event-dot-warn' : ' tt-event-dot-current') : ''}`} />
                                            {index < timeline.length - 1 && <span className='tt-event-line' />}
                                        </div>
                                        <div className='tt-event-body'>
                                            <span className='tt-title-small'>{eventLabel(event.type, pickup, tr)}</span>
                                            {eventReason(event, tr) && <span>{eventReason(event, tr)}</span>}
                                            <time className='tt-body-small' dateTime={event.timestamp}>{capitalize(tr.dateTime(event.timestamp))}</time>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    }

                    {(hasContact || branding.faq.length > 0) &&
                        <section className='tt-card' aria-labelledby='tt-help-title'>
                            <h2 id='tt-help-title' className='tt-title-medium'>{t('helpTitle')}</h2>

                            {hasContact &&
                                <div className='tt-contact'>
                                    {branding.phone &&
                                        <a href={`tel:${branding.phone.replace(/[^\d+]/g, '')}`}><Icon name='phone' />{t('call')}</a>
                                    }
                                    {whatsappNumber &&
                                        <a href={`https://wa.me/${whatsappNumber}`} target='_blank' rel='noopener noreferrer'><Icon name='whatsapp' />{t('whatsapp')}</a>
                                    }
                                    {branding.email &&
                                        <a href={`mailto:${branding.email}?subject=${encodeURIComponent(t('emailSubject', {code: view.trackTrace}))}`}><Icon name='email-outline' />{t('email')}</a>
                                    }
                                </div>
                            }

                            {branding.faq.length > 0 &&
                                <div className='tt-faq'>
                                    <h3 className='tt-visually-hidden'>{t('faqTitle')}</h3>
                                    {branding.faq.map((item, index) => (
                                        <div key={index} className='tt-faq-item'>
                                            <button
                                                type='button'
                                                className='tt-faq-question'
                                                aria-expanded={openFaq === index}
                                                aria-controls={`tt-faq-${index}`}
                                                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                            >
                                                <span>{item.question}</span>
                                                <Icon name='chevron-down' />
                                            </button>
                                            {openFaq === index && <p id={`tt-faq-${index}`} className='tt-faq-answer'>{item.answer}</p>}
                                        </div>
                                    ))}
                                </div>
                            }
                        </section>
                    }
                </main>

                <footer className='tt-footer'>
                    {branding.footerTitle && <p className='tt-title-small'>{branding.footerTitle}</p>}
                    {branding.footerMessage && <p className='tt-body-small tt-footer-message'>{branding.footerMessage}</p>}
                    {branding.companyAddress &&
                        <p className='tt-body-small'>{[branding.companyAddress.name, `${branding.companyAddress.street} ${branding.companyAddress.nr}${branding.companyAddress.addition}`.trim(), `${branding.companyAddress.postalCode} ${branding.companyAddress.city}`.trim()].filter(Boolean).join(' · ')}</p>
                    }
                    {updatedAt && <p className='tt-body-small'>{t('updatedAt', {time: tr.time(updatedAt)})}</p>}
                </footer>
            </div>

            {zoomed &&
                <div className='tt-lightbox' role='dialog' aria-modal='true' aria-label={t('photo', {n: ''}).trim()} onClick={() => setZoomed(null)}>
                    <button type='button' className='tt-lightbox-close' aria-label={t('close')} autoFocus onClick={() => setZoomed(null)}>
                        <Icon name='close' />
                    </button>
                    <img src={zoomed} alt='' onClick={(event) => event.stopPropagation()} />
                </div>
            }
        </div>
    )
}
